import cx from 'classnames'
import Styles from './YouTubeShortPreview.module.scss'
import { YouTubeIntegration } from 'api/clients/ExternalIntegrationsClient'
import { extractProfileInfo } from 'components/ExternalIntegrationSelect/utils'
import { MediaObject } from 'types'
import { isVideo } from 'components/ImagePickerModal/utils'
import { useVideoProgress } from '../hooks/useVideoProgress'
import { ReactNode } from 'react'
import { ReelsVideoPlayer } from '../components/ReelsVideoPlayer/ReelsVideoPlayer'
import { ConditionalWrapper } from 'common/ConditionalWrapper/ConditionalWrapper'
import { EditInDesignerOverlay } from '../components/EditInDesignerOverlay/EditInDesignerOverlay'
import { ScheduledPostShow } from 'api/clients/ScheduledPostsClient'
import { DesignChannel } from 'components/NewFileModal/Types'
import { Actions } from './Actions'
import { Footer } from './Footer'
import { TopActions } from './TopActions'

interface YouTubeShortPreviewProps {
  integration?: YouTubeIntegration
  mediaObjects?: MediaObject[]
  containerClassName?: string
  customPreviewContent?: ReactNode
  showVideoControls?: boolean
  isActive?: boolean
  isTransitioning?: boolean
  showHoverActionOverlay?: boolean
  almanacFileKey?: string
  post?: ScheduledPostShow
  isGenerating?: boolean
  onClose?: () => void
  alwaysTruncateCaption?: boolean
}

export const YouTubeShortPreview = ({
  integration,
  post,
  mediaObjects,
  containerClassName,
  customPreviewContent,
  showVideoControls,
  isActive = true,
  isTransitioning = false,
  isGenerating = false,
  showHoverActionOverlay = false,
  almanacFileKey,
  onClose,
  alwaysTruncateCaption = false,
}: YouTubeShortPreviewProps) => {
  const video = mediaObjects?.length && isVideo({ url: mediaObjects[0].source_url, type: mediaObjects[0].mime_type })
  const videoUrl = video ? mediaObjects[0].source_url : undefined
  const videoDuration = mediaObjects?.[0]?.duration

  const { videoRef, progress } = useVideoProgress({ videoUrl, duration: videoDuration })

  // username needed for ReelsVideoPlayer
  const { username } = integration ? extractProfileInfo(integration) : { username: 'user' }

  return (
    <ConditionalWrapper
      condition={showHoverActionOverlay && almanacFileKey}
      wrapper={(children) => (
        <EditInDesignerOverlay
          channel={DesignChannel.YoutubeShort}
          almanacFileKey={almanacFileKey as string}
          borderRadius={16}
          position={video ? 'top' : undefined}
          onClose={onClose}
          className={isGenerating ? Styles.disableHover : undefined}
        >
          {children}
        </EditInDesignerOverlay>
      )}
    >
      <div className={cx(Styles.container, containerClassName)}>
        <div className={Styles.content}>
          {customPreviewContent || (
            <ReelsVideoPlayer
              mediaObjects={mediaObjects}
              showVideoControls={showVideoControls}
              isActive={isActive}
              className={Styles.preview}
              dataTestId="youtube-short-video"
              username={username}
              videoRef={videoRef}
              almanacFileKey={almanacFileKey as string}
              isGenerating={isGenerating}
            />
          )}
        </div>
        <Footer
          integration={integration}
          post={post}
          isGenerating={isGenerating}
          alwaysTruncateCaption={alwaysTruncateCaption}
        />
        <TopActions />
        <Actions integration={integration} />
        <div className={Styles.loadingBar}>
          <div className={Styles.progress} style={{ width: `${progress}%` }} />
        </div>
      </div>
    </ConditionalWrapper>
  )
}
